import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { ShoppingBag, Store, Loader2 } from 'lucide-react';

export default function RoleSelection() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [role, setRole] = useState<'buyer' | 'seller'>('buyer');
  const [sellerDetails, setSellerDetails] = useState({
    businessName: '',
    businessDescription: '',
    businessCategory: '',
    phone: '',
  });

  // Get affiliate ref from URL or sessionStorage
  const affiliateRef = searchParams.get('ref') || sessionStorage.getItem('affiliate_ref');

  // Check if user is authenticated on mount
  useEffect(() => {
    const checkAuth = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        navigate('/auth');
        return;
      }

      // Check if user already has a role - fetch securely
      const [profileResult, roleResult] = await Promise.all([
        supabase.from('profiles').select('onboarding_completed').eq('id', user.id).single(),
        supabase.rpc('get_user_role', { _user_id: user.id })
      ]);
      
      const profile = profileResult.data;
      const userRole = roleResult.data;

      if (userRole && userRole !== 'buyer') {
        // User already has a role, redirect appropriately
        if (userRole === 'seller') {
          navigate('/seller/dashboard');
        } else if (userRole === 'admin') {
          navigate('/admin');
        }
      } else if (userRole === 'buyer' && profile?.onboarding_completed) {
        navigate('/buyer/orders');
      }
      
      setCheckingAuth(false);
    };

    checkAuth();
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        navigate('/auth');
        return;
      }

      // Update profile with additional details (NOT role - role is in user_roles table)
      const updateData: any = {
        onboarding_completed: true,
      };

      if (role === 'seller') {
        updateData.business_name = sellerDetails.businessName;
        updateData.business_description = sellerDetails.businessDescription;
        updateData.business_category = sellerDetails.businessCategory;
        updateData.phone = sellerDetails.phone;
        updateData.verified = true; // Auto-approve sellers
      }

      const { error: profileError } = await supabase
        .from('profiles')
        .update(updateData)
        .eq('id', user.id);

      if (profileError) throw profileError;

      // Insert role into user_roles table (upsert to handle existing role)
      const { error: roleError } = await supabase
        .from('user_roles')
        .upsert(
          { user_id: user.id, role: role },
          { onConflict: 'user_id,role' }
        );

      if (roleError) throw roleError;

      // Track affiliate referral if coming from an affiliate link
      if (affiliateRef) {
        try {
          // Find the affiliate link by code
          const { data: affiliateLink } = await supabase
            .from('affiliate_links')
            .select('id, active')
            .eq('code', affiliateRef)
            .eq('active', true)
            .single();

          if (affiliateLink) {
            // Record the referral
            await supabase
              .from('affiliate_referrals')
              .insert({
                affiliate_link_id: affiliateLink.id,
                referred_user_id: user.id,
              });
            
            // Clear the affiliate ref from sessionStorage
            sessionStorage.removeItem('affiliate_ref');
          }
        } catch (affiliateError) {
          console.warn('Failed to track affiliate referral:', affiliateError);
          // Don't block registration
        }
      }

      // Send welcome notification for new sellers
      if (role === 'seller') {
        try {
          await supabase.functions.invoke('send-transactional-email', {
            body: {
              type: 'seller-pending',
              to: user.email,
              data: {
                sellerName: user.user_metadata?.full_name || user.email?.split('@')[0] || 'Seller',
                businessName: sellerDetails.businessName
              }
            }
          });
        } catch (emailError) {
          console.warn('Email notification failed:', emailError);
          // Don't block the registration process
        }
      }

      toast({
        title: 'Welcome!',
        description: `Your ${role} account has been set up successfully.${role === 'seller' ? ' A confirmation email has been sent.' : ''}`,
      });

      // Redirect based on role
      if (role === 'seller') {
        navigate('/seller/dashboard');
      } else {
        navigate('/products');
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update profile',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  if (checkingAuth) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-muted p-4">
      <Card className="w-full max-w-2xl">
        <CardHeader className="text-center">
          <CardTitle className="text-3xl font-bold">Choose Your Role</CardTitle>
          <CardDescription className="text-lg">
            Select how you want to use Sabi Tradeng
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <RadioGroup
              value={role}
              onValueChange={(value) => setRole(value as 'buyer' | 'seller')}
              className="grid grid-cols-1 md:grid-cols-2 gap-4"
            >
              <label
                htmlFor="buyer"
                className={`relative flex flex-col items-center space-y-3 rounded-lg border-2 p-6 cursor-pointer transition-all ${
                  role === 'buyer'
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:border-primary/50'
                }`}
              >
                <RadioGroupItem value="buyer" id="buyer" className="sr-only" />
                <ShoppingBag className={`h-12 w-12 ${role === 'buyer' ? 'text-primary' : 'text-muted-foreground'}`} />
                <div className="text-center">
                  <p className="font-semibold">Buyer</p>
                  <p className="text-sm text-muted-foreground">
                    Browse and purchase products
                  </p>
                </div>
              </label>

              <label
                htmlFor="seller"
                className={`relative flex flex-col items-center space-y-3 rounded-lg border-2 p-6 cursor-pointer transition-all ${
                  role === 'seller'
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:border-primary/50'
                }`}
              >
                <RadioGroupItem value="seller" id="seller" className="sr-only" />
                <Store className={`h-12 w-12 ${role === 'seller' ? 'text-primary' : 'text-muted-foreground'}`} />
                <div className="text-center">
                  <p className="font-semibold">Seller</p>
                  <p className="text-sm text-muted-foreground">
                    List and sell your products
                  </p>
                </div>
              </label>
            </RadioGroup>

            {role === 'seller' && (
              <div className="space-y-4 animate-in slide-in-from-top-2 duration-300">
                <div className="space-y-2">
                  <Label htmlFor="businessName">Business Name *</Label>
                  <Input
                    id="businessName"
                    value={sellerDetails.businessName}
                    onChange={(e) =>
                      setSellerDetails({ ...sellerDetails, businessName: e.target.value })
                    }
                    placeholder="Enter your business name"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">Phone Number *</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={sellerDetails.phone}
                    onChange={(e) =>
                      setSellerDetails({ ...sellerDetails, phone: e.target.value })
                    }
                    placeholder="+234 123 456 7890"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="businessCategory">Business Category</Label>
                  <Input
                    id="businessCategory"
                    value={sellerDetails.businessCategory}
                    onChange={(e) =>
                      setSellerDetails({ ...sellerDetails, businessCategory: e.target.value })
                    }
                    placeholder="e.g., Electronics, Fashion, Food"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="businessDescription">Business Description</Label>
                  <Textarea
                    id="businessDescription"
                    value={sellerDetails.businessDescription}
                    onChange={(e) =>
                      setSellerDetails({ ...sellerDetails, businessDescription: e.target.value })
                    }
                    placeholder="Tell us about your business..."
                    rows={4}
                  />
                </div>
              </div>
            )}

            <Button
              type="submit"
              className="w-full"
              size="lg"
              disabled={loading || (role === 'seller' && !sellerDetails.businessName)}
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Setting up your account...
                </>
              ) : (
                `Continue as ${role === 'seller' ? 'Seller' : 'Buyer'}`
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}